import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { CellsConfig, DynamicTableCellType, DynamicTableData, DynamicTableRow, LazyTableDataSource } from '@rappi/ui/dynamic-table';
import { LazyScrollableDataSource } from '@rappi/common';
import { SelectComponent } from '@rappi/ui/select';
import { InputComponent } from '@rappi/ui/input';

import {
  COMPANY_STATUS,
  COMPANY_STATUS_LABELS,
  COMPANY_TYPE,
  COMPANY_TYPE_LABELS,
  COMPANY_TEXT_MAX_LENGTH,
  COMPANY_MULTIPLE_OWNERS,
  CompanyStatus,
  CompanyType,
} from './companies.constants';
import { CommercialOwnersDialogComponent } from '../commercial-owners-dialog/commercial-owners-dialog.component';

export interface Company {
  id: number;
  name: string;
  companyId: string;
  type: CompanyType;
  taxId: string;
  status: CompanyStatus;
  // One or more commercial owner emails. When more than one, the column shows the
  // COMPANY_MULTIPLE_OWNERS label as a link that opens the owners dialog.
  commercialOwners: string[];
  isTest: boolean;
}

interface FilterOption { id: string; name: string }

// Mock commercial owners reused across companies.
const OWNER_CAMILA = 'camila.juarez@rappi.com';
const OWNER_RODRIGO = 'rodrigo.casti@rappi.com';
const OWNER_BRUNO = 'bruno.marnt@rappi.com';

// Mock companies — names taken from the Figma design (node 1355:54998).
const MOCK_COMPANIES: Company[] = [
  { id: 1,  name: 'Philip Morris G',          companyId: 'M-143967', type: COMPANY_TYPE.MAKER_RETAILER,    taxId: '900.123.456', status: COMPANY_STATUS.TO_BE_DEFINED, commercialOwners: [OWNER_CAMILA],                        isTest: false },
  { id: 2,  name: 'Beiersdorf',               companyId: 'M-143968', type: COMPANY_TYPE.RETAILER,         taxId: '900.123.457', status: COMPANY_STATUS.TO_BE_DEFINED, commercialOwners: [OWNER_RODRIGO],                       isTest: false },
  { id: 3,  name: 'Nestlé Global',            companyId: 'M-143969', type: COMPANY_TYPE.SERVICE_PROVIDER, taxId: '900.123.458', status: COMPANY_STATUS.TO_BE_DEFINED, commercialOwners: [OWNER_CAMILA, OWNER_RODRIGO, OWNER_BRUNO], isTest: false },
  { id: 4,  name: 'Publicis Groups',          companyId: 'M-143970', type: COMPANY_TYPE.AGENCY,          taxId: '900.123.459', status: COMPANY_STATUS.TO_BE_DEFINED, commercialOwners: [OWNER_RODRIGO],                       isTest: false },
  { id: 5,  name: 'Corporate Varsal S.A.',    companyId: 'M-143971', type: COMPANY_TYPE.RETAILER,        taxId: '900.123.460', status: COMPANY_STATUS.TO_BE_DEFINED, commercialOwners: [OWNER_CAMILA],                        isTest: false },
  { id: 6,  name: 'The Coca Cola Company',    companyId: 'M-143972', type: COMPANY_TYPE.MAKER_RETAILER,  taxId: '900.123.461', status: COMPANY_STATUS.ACTIVE,        commercialOwners: [OWNER_BRUNO],                         isTest: false },
  { id: 7,  name: 'Grupo Bimbo Barcel',       companyId: 'M-143973', type: COMPANY_TYPE.MAKER,           taxId: '900.123.462', status: COMPANY_STATUS.ACTIVE,        commercialOwners: [OWNER_BRUNO],                         isTest: false },
  { id: 8,  name: 'PepsiCo Global',           companyId: 'M-143974', type: COMPANY_TYPE.MAKER_RETAILER,  taxId: '900.123.463', status: COMPANY_STATUS.PENDING,       commercialOwners: [OWNER_CAMILA, OWNER_RODRIGO],         isTest: false },
  { id: 9,  name: 'Mondelez Group',           companyId: 'M-143975', type: COMPANY_TYPE.MAKER,           taxId: '900.123.464', status: COMPANY_STATUS.PENDING,       commercialOwners: [OWNER_RODRIGO],                       isTest: false },
  { id: 10, name: 'M-1242357 Grupo Ferrero',  companyId: 'M-143976', type: COMPANY_TYPE.SERVICE_PROVIDER, taxId: '900.123.465', status: COMPANY_STATUS.REJECTED,      commercialOwners: [OWNER_RODRIGO],                       isTest: true  },
  { id: 11, name: 'Nutella Ferrero 2021',     companyId: 'M-143977', type: COMPANY_TYPE.MAKER_RETAILER,  taxId: '900.123.466', status: COMPANY_STATUS.INACTIVE,      commercialOwners: [OWNER_CAMILA],                        isTest: true  },
  { id: 12, name: 'Nutella Ferrero 2022',     companyId: 'M-143978', type: COMPANY_TYPE.MAKER_RETAILER,  taxId: '900.123.467', status: COMPANY_STATUS.ACTIVE,        commercialOwners: [OWNER_CAMILA],                        isTest: true  },
  { id: 13, name: 'Unilever Global',          companyId: 'M-143979', type: COMPANY_TYPE.MAKER,           taxId: '900.123.468', status: COMPANY_STATUS.ACTIVE,        commercialOwners: [OWNER_BRUNO],                         isTest: false },
  { id: 14, name: 'Procter & Gamble',         companyId: 'M-143980', type: COMPANY_TYPE.MAKER_RETAILER,  taxId: '900.123.469', status: COMPANY_STATUS.PENDING,       commercialOwners: [OWNER_CAMILA, OWNER_BRUNO],           isTest: false },
  { id: 15, name: 'Danone Latam',             companyId: 'M-143981', type: COMPANY_TYPE.RETAILER,        taxId: '900.123.470', status: COMPANY_STATUS.TO_BE_DEFINED, commercialOwners: [OWNER_RODRIGO],                       isTest: false },
  { id: 16, name: 'L\'Oréal Group',           companyId: 'M-143982', type: COMPANY_TYPE.AGENCY,          taxId: '900.123.471', status: COMPANY_STATUS.INACTIVE,      commercialOwners: [OWNER_CAMILA],                        isTest: false },
  { id: 17, name: 'Reckitt Benckiser',        companyId: 'M-143983', type: COMPANY_TYPE.MAKER,           taxId: '900.123.472', status: COMPANY_STATUS.REJECTED,      commercialOwners: [OWNER_BRUNO],                         isTest: true  },
  { id: 18, name: 'Colgate-Palmolive',        companyId: 'M-143984', type: COMPANY_TYPE.MAKER_RETAILER,  taxId: '900.123.473', status: COMPANY_STATUS.ACTIVE,        commercialOwners: [OWNER_RODRIGO],                       isTest: false },
];

// Mock option lists for the filters — derived from the mock companies so selections are functional.
const MOCK_COMPANY_NAMES: FilterOption[] = MOCK_COMPANIES.map(c => ({ id: c.name, name: c.name }));
const MOCK_COMPANY_TYPES: FilterOption[] = (Object.entries(COMPANY_TYPE_LABELS) as [CompanyType, string][]).map(([id, name]) => ({ id, name }));
const MOCK_STATUSES: FilterOption[] = (Object.entries(COMPANY_STATUS_LABELS) as [CompanyStatus, string][]).map(([id, name]) => ({ id, name }));
const MOCK_OWNERS: FilterOption[] = [OWNER_CAMILA, OWNER_RODRIGO, OWNER_BRUNO].map(email => ({ id: email, name: email }));

@Component({
  selector: 'app-companies',
  templateUrl: './companies.component.html',
  styleUrls: ['./companies.component.scss']
})
export class CompaniesComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('statusTemplate') statusTemplate: TemplateRef<any>;
  @ViewChild('ownerTemplate') ownerTemplate: TemplateRef<any>;
  @ViewChild('truncatedTextTemplate') truncatedTextTemplate: TemplateRef<any>;

  @ViewChild('nameSelect') private readonly nameSelect: SelectComponent<FilterOption>;
  @ViewChild('typeSelect') private readonly typeSelect: SelectComponent<FilterOption>;
  @ViewChild('statusSelect') private readonly statusSelect: SelectComponent<FilterOption>;
  @ViewChild('ownerSelect') private readonly ownerSelect: SelectComponent<FilterOption>;
  @ViewChild('searchInput') private readonly searchInput: InputComponent;

  tableData: DynamicTableData;

  // Filter selections (kept as the raw option arrays from one-select)
  filterNames: FilterOption[] = [];
  filterTypes: FilterOption[] = [];
  filterStatuses: FilterOption[] = [];
  filterOwners: FilterOption[] = [];
  filterIsTest = false;
  searchTerm = '';

  // Expose constants to the template (templates cannot import constants directly).
  readonly STATUS_LABELS = COMPANY_STATUS_LABELS;
  readonly TYPE_LABELS = COMPANY_TYPE_LABELS;
  readonly TEXT_MAX_LENGTH = COMPANY_TEXT_MAX_LENGTH;
  readonly MULTIPLE_OWNERS = COMPANY_MULTIPLE_OWNERS;

  readonly selectConfig = {
    search: true,
    includeAll: true,
    showTotalsChip: false,
    maxNumberOfChips: 1,
    panelClass: 'one-select'
  };

  readonly selectConfigNoSearch = {
    ...this.selectConfig,
    search: false
  };

  nameDataSource = new LazyScrollableDataSource(this.fetchNameOptions.bind(this));
  typeDataSource = new LazyScrollableDataSource(this.fetchTypeOptions.bind(this));
  statusDataSource = new LazyScrollableDataSource(this.fetchStatusOptions.bind(this));
  ownerDataSource = new LazyScrollableDataSource(this.fetchOwnerOptions.bind(this));

  private readonly PAGE_SIZE = 10;
  private readonly PAGE_SIZE_OPTIONS = [10, 25, 50];
  private readonly searchSubject = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$),
    ).subscribe(() => this.resetTable());
  }

  ngAfterViewInit(): void {
    this.tableData = this.buildTableConfig();
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearch(event: Event): void {
    const value = (event.target as HTMLInputElement).value.trim();
    this.searchTerm = value;
    this.searchSubject.next(value);
  }

  onNameChange(values: FilterOption[]): void {
    this.filterNames = values ?? [];
    this.resetTable();
  }

  onTypeChange(values: FilterOption[]): void {
    this.filterTypes = values ?? [];
    this.resetTable();
  }

  onStatusChange(values: FilterOption[]): void {
    this.filterStatuses = values ?? [];
    this.resetTable();
  }

  onOwnerChange(values: FilterOption[]): void {
    this.filterOwners = values ?? [];
    this.resetTable();
  }

  onTestToggle(): void {
    this.resetTable();
  }

  // Opens the Commercial Owners dialog with the given owner emails (Figma node 1370:61735).
  openOwnersDialog(owners: string[]): void {
    this.dialog.open(CommercialOwnersDialogComponent, {
      width: '373px',
      panelClass: 'poc-dialog-panel',
      data: { owners: owners ?? [] },
    });
  }

  private resetTable(): void {
    (this.tableData.dataSource as LazyTableDataSource<DynamicTableRow>).resetTable();
  }

  // Apply the current filter selections + search term to the mock companies.
  private applyFilters(): Company[] {
    let filtered = MOCK_COMPANIES;

    if (this.searchTerm) {
      const q = this.searchTerm.toLowerCase();
      filtered = filtered.filter(c =>
        c.name.toLowerCase().includes(q) ||
        c.companyId.toLowerCase().includes(q)
      );
    }
    if (this.filterNames.length) {
      const ids = new Set(this.filterNames.map(n => n.id));
      filtered = filtered.filter(c => ids.has(c.name));
    }
    if (this.filterTypes.length) {
      const ids = new Set(this.filterTypes.map(t => t.id));
      filtered = filtered.filter(c => ids.has(c.type));
    }
    if (this.filterStatuses.length) {
      const ids = new Set(this.filterStatuses.map(s => s.id));
      filtered = filtered.filter(c => ids.has(c.status));
    }
    if (this.filterOwners.length) {
      const ids = new Set(this.filterOwners.map(o => o.id));
      filtered = filtered.filter(c => c.commercialOwners.some(o => ids.has(o)));
    }
    if (this.filterIsTest) {
      filtered = filtered.filter(c => c.isTest);
    }
    return filtered;
  }

  private buildTableConfig(): DynamicTableData {
    const dataSource = new LazyTableDataSource<DynamicTableRow>(
      this.fetchCompanies.bind(this),
      this.PAGE_SIZE,
      'name',
      1
    );

    const columns = ['name', 'companyId', 'type', 'taxId', 'status', 'commercialOwner'];

    const cellsConfig: CellsConfig = {
      name:           { type: DynamicTableCellType.templateRef, title: 'Company name' },
      companyId:      { type: DynamicTableCellType.templateRef, title: 'Company ID' },
      type:           { type: DynamicTableCellType.templateRef, title: 'Company type' },
      taxId:          { type: DynamicTableCellType.templateRef, title: 'Tax id' },
      status:         { type: DynamicTableCellType.templateRef, title: 'Status' },
      commercialOwner:{ type: DynamicTableCellType.templateRef, title: 'Commercial owner' },
    };

    return {
      dataSource,
      hideSearch: true,
      pagination: true,
      lazyLoaded: true,
      message: { title: 'No companies found' },
      paginatorOpts: { pageSize: this.PAGE_SIZE, pageSizeOptions: this.PAGE_SIZE_OPTIONS },
      columns,
      cellsConfig
    };
  }

  private fetchCompanies(page: number, limit: number): Observable<DynamicTableRow[]> {
    const filtered = this.applyFilters();
    const start = (page - 1) * limit;
    const rows: DynamicTableRow[] = filtered.slice(start, start + limit).map(c => this.toRow(c));

    return of(rows).pipe(
      map(() => {
        if (this.tableData?.paginator) {
          this.tableData.paginator.length = filtered.length;
        }
        return rows;
      })
    );
  }

  private toRow(c: Company): DynamicTableRow {
    const isMultiple = c.commercialOwners.length > 1;
    const ownerValue = isMultiple ? this.MULTIPLE_OWNERS : (c.commercialOwners[0] ?? '');
    return {
      name:            { templateRef: this.truncatedTextTemplate, context: { value: c.name, maxLength: this.TEXT_MAX_LENGTH.name } },
      companyId:       { templateRef: this.truncatedTextTemplate, context: { value: c.companyId, maxLength: this.TEXT_MAX_LENGTH.companyId } },
      type:            { templateRef: this.truncatedTextTemplate, context: { value: this.TYPE_LABELS[c.type], maxLength: this.TEXT_MAX_LENGTH.type } },
      taxId:           { templateRef: this.truncatedTextTemplate, context: { value: c.taxId, maxLength: this.TEXT_MAX_LENGTH.taxId } },
      status:          { templateRef: this.statusTemplate, context: { label: this.STATUS_LABELS[c.status], code: c.status } },
      commercialOwner: { templateRef: this.ownerTemplate, context: { value: ownerValue, owners: c.commercialOwners, maxLength: this.TEXT_MAX_LENGTH.commercialOwner } },
    };
  }

  // ── Filter option fetchers (mock) ───────────────────────────────────────────
  // Paginate the mock lists and apply the dropdown's internal search so the one-select
  // search box works. LazyScrollableDataSource concatenates pages, so return each page slice.

  private fetchNameOptions(page = 0, limit = 20, search = ''): Observable<FilterOption[]> {
    return of(this.paginateOptions(MOCK_COMPANY_NAMES, page, limit, search));
  }

  private fetchTypeOptions(page = 0, limit = 20, search = ''): Observable<FilterOption[]> {
    return of(this.paginateOptions(MOCK_COMPANY_TYPES, page, limit, search));
  }

  private fetchStatusOptions(page = 0, limit = 20, search = ''): Observable<FilterOption[]> {
    return of(this.paginateOptions(MOCK_STATUSES, page, limit, search));
  }

  private fetchOwnerOptions(page = 0, limit = 20, search = ''): Observable<FilterOption[]> {
    return of(this.paginateOptions(MOCK_OWNERS, page, limit, search));
  }

  private paginateOptions(options: FilterOption[], page: number, limit: number, search: string): FilterOption[] {
    const q = (search || '').toLowerCase();
    const filtered = q ? options.filter(o => o.name.toLowerCase().includes(q)) : options;
    const start = page * limit;
    return filtered.slice(start, start + limit);
  }
}
